import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import i18n from '../i18n/index.js';
import { supabase, setActiveTenant } from '../services/supabase';
import { logAct } from '../services/masterActsLog';
import { DEFAULT_TENANT, GLOBAL_PLATFORM_CONTEXT, applyTenantTheme } from './tenantConstants';
import { hasSystemRole } from '../utils/roleAccess';
import { setActiveCurrency } from '../utils/currency';
import { ACADEMY_SLUGS } from '../services/academyReadinessEngine';
import { chargerEtatCommercial, definirEtatHorsTenant } from '../services/gardeExport';
import { applyTenantDefaults, applyTenantTerms } from '../services/tenantTerms';
import { modeleEffectif } from '../services/modelesAcademiques';
import { regleDeCalcul, definirRegleActive } from '../services/moteurCalculResultats';

const TenantContext = createContext(null);

// ponytail: timeout sur les appels réseau bloquants de loadTenant — sans lui,
// un Supabase injoignable (RLS en boucle, proxy muet, token expiré sans
// rejet) laisse `tenantLoading` à `true` pour toujours et l'app reste
// bloquée sur "Initialisation sécurisée..." (constaté sur /dashboard/*).
const withTimeout = (promise, ms = 10000) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => reject(new Error('tenant_load_timeout')), ms)),
]);

// Modèle académique EFFECTIF de la session (choix du tenant, sinon repli lu sans écriture) : mentions,
// lettres et points de GPA se lisent de lui (M7-P0-5). Une lecture impossible ne bloque pas le tenant :
// null, et les mentions restent vides plutôt que devinées.
const lireModeleEffectif = async () => {
  try { return await withTimeout(modeleEffectif()); } catch { return null; }
};

const computeLicenseStatus = (tenant) => {
  const end = tenant?.license_end;
  if (!end) return { isLicenseExpired: false, isExpiringSoon: false, daysUntilExpiry: null };
  const days = Math.ceil((new Date(end) - new Date()) / 86400000);
  return {
    isLicenseExpired: days < 0,
    isExpiringSoon: days >= 0 && days <= 30,
    daysUntilExpiry: days,
  };
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenantState] = useState(DEFAULT_TENANT);
  const [tenantLoading, setTenantLoading] = useState(true);

  // Devise du tenant (tenants.currency) propagée au formateur global fmtMoney, puis aux chaînes i18n
  // (`{{devise}}`, `{{tenant_city}}`) : plus de « FCFA » ni de ville écrits dans les libellés.
  useEffect(() => {
    setActiveCurrency(tenant?.currency);
    applyTenantDefaults({ currency: tenant?.currency, city: tenant?.city });
  }, [tenant?.currency, tenant?.city]);
  // Règle de calcul des résultats du tenant (moteur unique, M7-P0-1) : les calculs qui ne reçoivent pas le tenant la lisent.
  useEffect(() => { definirRegleActive(tenant?.id ? regleDeCalcul(tenant) : null); }, [tenant]);
  // Intitulé du chef d'école choisi par le tenant -> variable i18n `{{school_head}}`.
  useEffect(() => { applyTenantTerms(tenant?.document_settings); }, [tenant?.document_settings]);
  const [inspectedTenant, setInspectedTenant] = useState(() => {
    try {
      const saved = localStorage.getItem('rectorium_inspected_tenant');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const loadTenant = async () => {
    try {
      const userStr = localStorage.getItem('rectorium_user');
      const user = userStr ? JSON.parse(userStr) : null;
      const isPlatformAdmin = user && hasSystemRole(user, 'master') && !user.tenant_id;

      if (isPlatformAdmin) {
        const inspectedStr = localStorage.getItem('rectorium_inspected_tenant');
        if (inspectedStr) {
          const inspected = JSON.parse(inspectedStr);
          // RÉ-OUVRIR le contexte serveur avant de restaurer l'affichage : un
          // état repris du localStorage sans session serveur est un bandeau
          // fantôme (constaté le 2026-08-21 : 9 jours de « MODE INSPECTION »
          // client pendant que private.master_jwt_sessions était en 'normal' —
          // toute élévation refusait, à bon droit). Fail-closed : si le serveur
          // n'enregistre pas l'inspection, on purge et on retombe en contexte
          // plateforme, on n'affiche JAMAIS une inspection que lui ignore.
          let reopened = false;
          try {
            const { error } = await withTimeout(supabase.rpc('start_master_tenant_inspection', {
              p_target_tenant_id: inspected.id,
              p_reason: "Reprise d'inspection après rechargement",
            }));
            reopened = !error;
            if (error) console.error('Reprise d\'inspection refusée par le serveur :', error.message);
          } catch (e) {
            console.error('Reprise d\'inspection impossible :', e);
          }
          if (reopened) {
            inspected.modele_academique = await lireModeleEffectif();
            setActiveTenant(inspected);
            setTenantState(inspected);
            applyTenantTheme(inspected);
            setTenantLoading(false);
            return;
          }
          localStorage.removeItem('rectorium_inspected_tenant');
          setInspectedTenant(null);
          logAct({ type: 'tenant_action', scope: 'inspection', target: inspected?.slug || String(inspected?.id || ''), payload: { raison: 'reprise_refusee_par_le_serveur' }, result: 'inspection_purgee' });
          setActiveTenant(GLOBAL_PLATFORM_CONTEXT);
          setTenantState(GLOBAL_PLATFORM_CONTEXT);
          applyTenantTheme(GLOBAL_PLATFORM_CONTEXT);
          setTenantLoading(false);
          return;
        } else {
          setActiveTenant(GLOBAL_PLATFORM_CONTEXT);
          setTenantState(GLOBAL_PLATFORM_CONTEXT);
          applyTenantTheme(GLOBAL_PLATFORM_CONTEXT);
          setTenantLoading(false);
          return;
        }
      }

      // Visiteur anonyme (landing, signup, login) : aucun tenant chargé —
      // branding neutre Rectorium, jamais celui d'un tenant arbitraire.
      if (!user) {
        setActiveTenant(DEFAULT_TENANT);
        setTenantState(DEFAULT_TENANT);
        document.title = i18n.t('tenant_context.default_document_title');
        setTenantLoading(false);
        return;
      }

      // Normal tenant-scoped user
      const buildQuery = () => {
        const q = supabase.from('tenants').select('*');
        return user?.tenant_id ? q.eq('id', user.tenant_id) : q.eq('is_active', true).limit(1);
      };
      
      // Une lecture lente ou refusée ne doit pas figer le tenant PAR DÉFAUT
      // (« Rectorium » démo) pour toute la session : l'utilisateur voyait alors
      // les données d'un autre contexte (forums illisibles, bandeau « Rectorium »
      // sur un compte d'un autre établissement). On réessaie, puis on retombe sur le défaut.
      let data = null;
      for (let essai = 0; essai < 3 && !data; essai++) {
        if (essai > 0) await new Promise((r) => setTimeout(r, 1500 * essai));
        try {
          const res = await withTimeout(buildQuery().maybeSingle());
          if (res.error) console.error('Tenant : lecture refusée', res.error.message);
          data = res.data || null;
        } catch (e) {
          console.error('Tenant : lecture trop lente ou impossible', e);
        }
      }

      if (data) {
        const merged = { ...DEFAULT_TENANT, ...data };
        merged.grading_system    = { ...DEFAULT_TENANT.grading_system,    ...(data.grading_system    || {}) };
        merged.credit_system     = { ...DEFAULT_TENANT.credit_system,     ...(data.credit_system     || {}) };
        merged.governance_config = { ...DEFAULT_TENANT.governance_config, ...(data.governance_config || {}) };
        merged.modele_academique = await lireModeleEffectif();
        
        // Map document settings fallbacks from JSONB column
        merged.doc_header_fr = data.document_settings?.doc_header_fr || data.doc_header_fr;
        merged.doc_header_en = data.document_settings?.doc_header_en || data.doc_header_en;
        merged.doc_footer_fr = data.document_settings?.doc_footer_fr || data.doc_footer_fr;
        merged.doc_footer_en = data.document_settings?.doc_footer_en || data.doc_footer_en;

        setActiveTenant(merged);
        setTenantState(merged);
        applyTenantTheme(merged);
      } else {
        setActiveTenant(DEFAULT_TENANT);
        applyTenantTheme(DEFAULT_TENANT);
      }
    } catch {
      applyTenantTheme(DEFAULT_TENANT);
    } finally {
      setTenantLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTenant(); }, []);

  /**
   * L'ÉTAT COMMERCIAL, CHARGÉ DÈS QUE LE TENANT EST RÉSOLU.
   *
   * `gardeExport` REFUSE tant qu'il ne connaît pas l'état — repli qui n'accorde
   * pas. Sans ce chargement, les exports lèveraient « état inconnu » pour tout
   * le monde : la garde serait un bouton mort, pas une règle commerciale. On
   * rejoue à chaque changement de tenant, l'inspection Master en changeant.
   */
  useEffect(() => {
    if (tenantLoading) return;
    // Sans tenant résolu — shell Master global, page publique — il n'y a rien
    // à demander au serveur : l'absence de contexte est un fait, pas un
    // verdict commercial. On évite l'aller-retour.
    if (!tenant?.id) { definirEtatHorsTenant(); return; }
    chargerEtatCommercial(supabase);
  }, [tenant?.id, tenantLoading]);

  const startTenantInspection = async (targetTenant) => {
    // Ouvre le contexte effectif côté serveur (session_mode='master_inspect',
    // lecture seule) — c'est cet appel, pas le state client ci-dessous, qui
    // fait réellement foi pour get_request_tenant_id()/RLS/élévation.
    // supabase.rpc() ne REJETTE pas : l'échec arrive dans `{ error }`, le lire
    // est la seule façon de l'entendre — l'ancien try/catch+warn n'attrapait
    // rien et installait un bandeau que le serveur ignorait (2026-08-21).
    // Fail-closed : refus serveur → AUCUN état client, le non-basculement est
    // le signal honnête.
    try {
      const { error } = await supabase.rpc('start_master_tenant_inspection', {
        p_target_tenant_id: targetTenant.id,
        p_reason: 'Inspection depuis le Master Shell',
      });
      if (error) {
        console.error('Inspection refusée par le serveur :', error.message);
        logAct({ type: 'tenant_action', scope: 'inspection', target: targetTenant?.slug || String(targetTenant?.id || ''), payload: { erreur: error.message }, result: 'inspection_refusee' });
        return;
      }
    } catch (e) {
      console.error('start_master_tenant_inspection injoignable :', e);
      logAct({ type: 'tenant_action', scope: 'inspection', target: targetTenant?.slug || String(targetTenant?.id || ''), payload: { erreur: String(e?.message || e) }, result: 'inspection_refusee' });
      return;
    }

    targetTenant = { ...targetTenant, modele_academique: await lireModeleEffectif() };
    setInspectedTenant(targetTenant);
    localStorage.setItem('rectorium_inspected_tenant', JSON.stringify(targetTenant));
    setActiveTenant(targetTenant);
    setTenantState(targetTenant);
    applyTenantTheme(targetTenant);

    // Preuve de supervision RÉELLE pour le tenant Academy (readiness Master —
    // academyReadinessEngine.js lit ce même `audit_logs` : ne JAMAIS marquer
    // `data_verified` à `true` sans une requête scopée réellement exécutée et
    // réussie, sous peine de reproduire le bug des booléens sans preuve).
    let inspectionMetadata = {
      previous_context: 'PLATFORM_CONTEXT',
      new_context: 'INSPECTION_CONTEXT',
      reason: 'Start tenant inspection'
    };
    if (ACADEMY_SLUGS.includes(targetTenant?.slug) && targetTenant?.id) {
      try {
        const { count, error } = await supabase
          .from('campuses')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', targetTenant.id);
        // Ce que ce comptage prouve, et ce qu'il ne prouve PAS — à lire avant
        // de s'appuyer sur `data_verified` :
        //
        // FERMÉ — les pannes qui REMONTENT. Table absente, colonne inconnue,
        //   réseau coupé, jeton expiré : tout cela arrive dans `error`. Et un
        //   Content-Range absent ou tronqué par un proxy rend `count = null`
        //   SANS `error`. Dans ces deux cas le comptage n'a rien établi : la
        //   preuve vaut false et le compte vaut null — jamais 0, qui ferait
        //   passer une panne pour un tenant Academy réellement sans campus.
        //
        // PAS FERMÉ — le FILTRAGE RLS, et c'est le cas le PLUS probable des
        //   deux. `mt_tenant_isolation ON public.campuses` ne lève aucune
        //   erreur : elle RETIRE les lignes. PostgREST répond 200, error=null,
        //   count=0. Un Master qui ne lit RIEN est alors indiscernable, sur
        //   cette seule ligne d'audit, d'un tenant sans campus. On ne peut donc
        //   PAS le détecter ici — on le rend seulement lisible en aval, en
        //   écrivant le compte tel quel : c'est `academyReadinessEngine` qui
        //   exige un compte STRICTEMENT positif avant de créditer
        //   `masterCanInspect`, au lieu de se fier à `data_verified` seul.
        if (error) {
          console.error('Comptage des campus du tenant inspecté refusé :', error.message, '— la preuve de supervision restera non concluante ; vérifier que la table public.campuses est lisible dans le contexte master_inspect avant de rejouer l\'inspection.');
        } else if (count === null) {
          console.error("Comptage des campus du tenant inspecté sans total exploitable (en-tête Content-Range absent ou tronqué, souvent un proxy intermédiaire) — la preuve de supervision restera non concluante ; rejouer l'inspection en accès direct au projet Supabase.");
        }
        // `data_verified` ne vaut true que si le comptage a RÉPONDU un nombre :
        // sans erreur ET sans count null. Sinon les deux champs de la même
        // ligne d'audit se contrediraient (« vérifié » avec un total inconnu).
        const comptageConcluant = !error && count !== null;
        inspectionMetadata = {
          ...inspectionMetadata,
          data_verified: comptageConcluant,
          verified_row_count: comptageConcluant ? count : null,
        };
      } catch (e) {
        console.error('Comptage des campus du tenant inspecté injoignable :', e);
        inspectionMetadata = { ...inspectionMetadata, data_verified: false, verified_row_count: null };
      }
    }

    try {
      const userStr = localStorage.getItem('rectorium_user');
      const user = userStr ? JSON.parse(userStr) : null;
      // supabase-js ne REJETTE pas sur un refus de la base : le refus arrive
      // dans `{ error }`. Sans le lire, le catch ci-dessous est MORT pour tout
      // refus (colonne inconnue, RLS en écriture, contrainte) et la preuve
      // qu'on vient de rendre honnête peut ne jamais s'écrire, sans une seule
      // ligne de journal. Ce n'est pas théorique : ces écritures visaient
      // entity_type/entity_id/metadata au lieu de table_name/record_id/payload
      // et échouaient donc en silence depuis des mois — la preuve de
      // supervision n'existait pas et rien ne le disait
      // (GUIDE_RESOLUTION_PROBLEMES, 42.2).
      const { error: auditError } = await supabase.from('audit_logs').insert({
        user_id: user?.id,
        action: 'MASTER_CONTEXT_SWITCH',
        tenant_id: targetTenant.id,
        table_name: 'tenants',
        record_id: targetTenant.id,
        payload: inspectionMetadata
      });
      if (auditError) {
        console.error("Preuve d'inspection NON enregistrée dans audit_logs :", auditError.message, "— la readiness Academy restera bloquée sur « inspection_not_verified » tant que ce refus dure ; vérifier les colonnes attendues (table_name/record_id/payload) et le droit d'INSERT du Master sur public.audit_logs.");
      }
    } catch (e) {
      console.error("Écriture de la preuve d'inspection injoignable :", e, "— audit_logs n'a pas répondu ; vérifier la connectivité au projet Supabase puis rejouer l'inspection, la preuve de supervision manque.");
    }
    logAct({ type: 'tenant_action', scope: 'inspection', target: targetTenant?.slug || String(targetTenant?.id || ''), payload: { tenant_name: targetTenant?.name || null }, result: 'inspection_started' });
  };

  const stopTenantInspection = async () => {
    const prev = inspectedTenant;
    // À la sortie, nettoyer le client même si le serveur échoue reste le sens
    // SÛR (moins de contexte affiché que de contexte réel) — mais l'échec est
    // désormais LU et journalisé au lieu d'être avalé.
    try {
      const { error } = await supabase.rpc('stop_master_tenant_inspection');
      if (error) console.warn('stop_master_tenant_inspection refusée :', error.message);
    } catch (e) {
      console.warn('stop_master_tenant_inspection injoignable :', e);
    }
    setInspectedTenant(null);
    localStorage.removeItem('rectorium_inspected_tenant');
    setActiveTenant(GLOBAL_PLATFORM_CONTEXT);
    setTenantState(GLOBAL_PLATFORM_CONTEXT);
    applyTenantTheme(GLOBAL_PLATFORM_CONTEXT);

    // Preuve de préservation du contexte Master RÉELLE (uniquement pour la
    // sortie du tenant Academy — évite un aller-retour DB à chaque sortie de
    // tenant ordinaire). Deux vérifications réelles : (1) la session Auth du
    // Master survit intacte à la bascule (droits Master conservés) ; (2) la
    // requête scopée sur l'ancien tenant_id renvoie encore des données
    // cohérentes SEULEMENT quand explicitement filtrée — preuve que le
    // contexte actif a bien changé (pas de filtre resté collé en silence).
    let restoreMetadata = {
      previous_context: 'INSPECTION_CONTEXT',
      new_context: 'PLATFORM_CONTEXT',
      reason: 'Stop tenant inspection'
    };
    if (ACADEMY_SLUGS.includes(prev?.slug) && prev?.id) {
      try {
        const { data: session } = await supabase.auth.getSession();
        const masterSessionIntact = Boolean(session?.session?.user?.id);
        const { error: scopedErr } = await supabase
          .from('campuses')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', prev.id);
        restoreMetadata = {
          ...restoreMetadata,
          context_restored: masterSessionIntact,
          cross_tenant_leak_detected: Boolean(scopedErr),
        };
      } catch {
        restoreMetadata = { ...restoreMetadata, context_restored: false, cross_tenant_leak_detected: true };
      }
    }

    try {
      const userStr = localStorage.getItem('rectorium_user');
      const user = userStr ? JSON.parse(userStr) : null;
      // Même défaut, même correctif qu'à l'entrée d'inspection : sans lire
      // `error`, un refus de la base ne rejette pas et le catch ne voit rien —
      // la preuve de restitution du contexte disparaît en silence.
      const { error: auditError } = await supabase.from('audit_logs').insert({
        user_id: user?.id,
        action: 'MASTER_CONTEXT_SWITCH',
        tenant_id: prev?.id || null,
        table_name: 'tenants',
        record_id: prev?.id || null,
        payload: restoreMetadata
      });
      if (auditError) {
        console.error("Preuve de sortie d'inspection NON enregistrée dans audit_logs :", auditError.message, "— la readiness Academy restera bloquée sur « context_preservation_not_verified » ; vérifier les colonnes attendues (table_name/record_id/payload) et le droit d'INSERT du Master sur public.audit_logs.");
      }
    } catch (e) {
      console.error("Écriture de la preuve de sortie d'inspection injoignable :", e, "— audit_logs n'a pas répondu ; vérifier la connectivité au projet Supabase, la preuve de restitution du contexte manque.");
    }
    logAct({ type: 'tenant_action', scope: 'inspection', target: prev?.slug || String(prev?.id || ''), payload: { tenant_name: prev?.name || null }, result: 'inspection_stopped' });
  };

  const updateTenant = async (updates) => {
    // Intercept document header and footer changes and serialize them inside document_settings JSONB
    const document_settings = {
      ...(tenant.document_settings || {}),
      ...(updates.document_settings || {})
    };

    if ('doc_header_fr' in updates) { document_settings.doc_header_fr = updates.doc_header_fr; }
    if ('doc_header_en' in updates) { document_settings.doc_header_en = updates.doc_header_en; }
    if ('doc_footer_fr' in updates) { document_settings.doc_footer_fr = updates.doc_footer_fr; }
    if ('doc_footer_en' in updates) { document_settings.doc_footer_en = updates.doc_footer_en; }

    const cleanUpdates = { ...updates, document_settings };
    delete cleanUpdates.doc_header_fr;
    delete cleanUpdates.doc_header_en;
    delete cleanUpdates.doc_footer_fr;
    delete cleanUpdates.doc_footer_en;

    const updated = { ...tenant, ...updates };
    setActiveTenant(updated);
    setTenantState(updated);
    applyTenantTheme(updated);

    const payload = { ...cleanUpdates, updated_at: new Date().toISOString() };

    if (tenant?.id) {
      const { error } = await supabase.from('tenants').update(payload).eq('id', tenant.id);
      if (error) throw error;
    } else {
      const { data } = await supabase
        .from('tenants')
        .insert([{ slug: tenant?.slug || DEFAULT_TENANT.slug || 'rectorium-demo', ...DEFAULT_TENANT, ...cleanUpdates }])
        .select()
        .single();
      if (data) {
        const withId = { ...updated, id: data.id };
        setActiveTenant(withId);
        setTenantState(withId);
      }
    }
  };

  const licenseStatus = useMemo(() => computeLicenseStatus(tenant), [tenant]);

  const value = useMemo(() => ({
    tenant,
    setTenant: setTenantState,
    updateTenant,
    reloadTenant: loadTenant,
    tenantLoading,
    inspectedTenant,
    startTenantInspection,
    stopTenantInspection,
    ...licenseStatus
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tenant, tenantLoading, inspectedTenant, licenseStatus]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTenant = () => useContext(TenantContext);

// eslint-disable-next-line react-refresh/only-export-components
export { DEFAULT_TENANT, GLOBAL_PLATFORM_CONTEXT, applyTenantTheme } from './tenantConstants';
